import { defineNuxtRouteMiddleware, useRuntimeConfig } from '#imports'
import type { RouteLocationRaw } from 'vue-router'

import { merge } from '@backmarket/utils/object/merge'

import { useRedirectLocale } from '../composables/useRedirectLocale'
import { useUserStore } from '../composables/useUserStore'
import { findRouteOrParentMatchingPredicate } from '../utils/routes'

/**
 * The authenticated middleware.
 *
 * It makes sure that people are authenticated.
 *
 * This middleware is globally available as soon as you register the OAuth
 * module in your application. You do not have to do anything to start
 * filtering page views.
 *
 * In order to prevent access to a page to non-authenticated people, you have
 * to provide some route meta configuration using the `meta.authenticated` property.
 *
 * Only the `required` value is mandatory. If set to `true`, access will be
 * prevented to non-authenticated people. If set to `false`, then everyone will
 * be able to access the page (both authenticated and unauthenticated users).
 *
 * @example
 * const route = {
 *   name: 'dashboard',
 *   path: '/dashboard',
 *   component: () => import('./components/Dashboard'),
 *   meta: {
 *     authenticated: {
 *       required: true,
 *     },
 *   },
 * }
 *
 * If the access is prevented, the user will be redirected to the redirect
 * location configured when the module was instantiated. This is configured
 * via the `defaultRedirectLocationWhenUnauthenticated` module option.
 *
 * However, it is possible to define a custom redirection location for a
 * specific route. This is achieved via the `meta.authenticated.redirection` property.
 *
 * @example
 * const route = {
 *   name: 'dashboard',
 *   path: '/dashboard',
 *   component: () => import('./components/Dashboard'),
 *   meta: {
 *     authenticated: {
 *       required: true,
 *       redirection: { name: 'login' }
 *     },
 *   },
 * }
 */

declare module 'vue-router' {
  interface RouteMeta {
    authenticated?: {
      required: boolean
      redirection?: Exclude<RouteLocationRaw, string>
    }
    unauthenticated?: {
      required: boolean
      redirection?: Exclude<RouteLocationRaw, string>
    }
  }
}

export default defineNuxtRouteMiddleware(async (route) => {
  const config = useRuntimeConfig().public.oauth
  const { isUserFetched, isUserAuthenticated, fetchUser } = useUserStore()

  const matchedRouteWithAuthenticated = findRouteOrParentMatchingPredicate(
    route,
    (routeDefinition) =>
      !!(routeDefinition.meta && routeDefinition.meta.authenticated),
  )

  // We may request a routes tree not protected at all.
  if (matchedRouteWithAuthenticated === undefined) return
  if (matchedRouteWithAuthenticated.meta.authenticated === undefined) return
  if (matchedRouteWithAuthenticated.meta.authenticated.required === false)
    return

  // We don't want to fire the request each time the middleware is instanciated.
  // If the authentication status was previously fetched already, there is no
  // need to fetch it again, and we should use the data saved in our store.
  if (!isUserFetched.value) {
    await fetchUser()
  }

  // We're good to go, we're authenticated.
  if (isUserAuthenticated.value) return

  const { getRouteLocationWithLocale } = useRedirectLocale()

  const redirect =
    matchedRouteWithAuthenticated.meta.authenticated.redirection ||
    getRouteLocationWithLocale(
      config.defaultRedirectLocationWhenUnauthenticated,
    )
  const redirectWithNext = merge({}, redirect, {
    query: { next: route.fullPath },
  })

  // We are expected to return different values based on the expected behaviour.
  // eslint-disable-next-line consistent-return
  return redirectWithNext
})
